import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { logger } from '@/lib/logger';

/**
 * Consent Context — GDPR cookie consent manager.
 *
 * Categories:
 *   - necessary:   Always true. Session cookies (auth tokens). No toggle.
 *   - analytics:   Opt-in. Reserved for future PostHog / Plausible / similar.
 *   - marketing:   Opt-in. Reserved for future re-targeting (none currently used).
 *
 * Storage: localStorage key "mb_consent_v1".
 * Default state for first-time visitors: analytics=false, marketing=false (strict GDPR).
 */

const STORAGE_KEY = 'mb_consent_v1';
const POLICY_VERSION = '1.0';

const DEFAULT_STATE = {
  necessary: true,
  analytics: false,
  marketing: false,
  policy_version: null,  // null => never decided
  decided_at: null,
};

const ConsentContext = createContext(null);

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      policy_version: parsed.policy_version || null,
      decided_at: parsed.decided_at || null,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function ConsentProvider({ children }) {
  const [state, setState] = useState(readStoredConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const hasDecided = state.policy_version === POLICY_VERSION;

  const persist = useCallback((next, action) => {
    const payload = {
      necessary: true,
      analytics: Boolean(next.analytics),
      marketing: Boolean(next.marketing),
      policy_version: POLICY_VERSION,
      decided_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setState(payload);
    // Fire-and-forget audit log to backend
    api.post('/consent', {
      categories: {
        necessary: true,
        analytics: payload.analytics,
        marketing: payload.marketing,
      },
      action,
      policy_version: POLICY_VERSION,
    }).catch(err => logger.warn('Consent log failed:', err?.message));
  }, []);

  const acceptAll = useCallback(() => {
    persist({ analytics: true, marketing: true }, 'accept_all');
    setPreferencesOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ analytics: false, marketing: false }, 'reject_all');
    setPreferencesOpen(false);
  }, [persist]);

  const savePreferences = useCallback((choices) => {
    persist({ analytics: Boolean(choices.analytics), marketing: Boolean(choices.marketing) }, 'save');
    setPreferencesOpen(false);
  }, [persist]);

  const withdraw = useCallback(() => {
    persist({ analytics: false, marketing: false }, 'withdraw');
  }, [persist]);

  const hasConsent = useCallback((category) => {
    if (category === 'necessary') return true;
    if (!hasDecided) return false;
    return Boolean(state[category]);
  }, [state, hasDecided]);

  // Emit a DOM event so non-React code (e.g., future analytics init) can react.
  useEffect(() => {
    if (!hasDecided) return;
    window.dispatchEvent(new CustomEvent('mb:consent-change', { detail: { ...state } }));
  }, [state, hasDecided]);

  const value = useMemo(() => ({
    state,
    hasDecided,
    hasConsent,
    acceptAll,
    rejectAll,
    savePreferences,
    withdraw,
    preferencesOpen,
    openPreferences: () => setPreferencesOpen(true),
    closePreferences: () => setPreferencesOpen(false),
  }), [state, hasDecided, hasConsent, acceptAll, rejectAll, savePreferences, withdraw, preferencesOpen]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
  return ctx;
}
